import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import { useTranslation } from "@shared/utils/internationalization";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import Input from "@ui-kit/form/Input/Input.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import useAuthService from "@shared/modules/auth/hooks/useAuthService";
import type { PasswordResetFormSharedProps } from "./PasswordResetForm.props";

export type PasswordResetFormProps = PasswordResetFormSharedProps & {
  // Add any web specific props here.
};

type PasswordResetFormData = {
  email: string;
};

export default function PasswordResetForm(
  props: PasswordResetFormProps
): JSX.Element {
  const { onSuccess, setEmailUsed } = props;
  const { t } = useTranslation("common");
  const { usePasswordReset } = useAuthService();

  const passwordResetSchema = yup.object().shape({
    email: yup
      .string()
      .required(t("Email is required"))
      .email(t("Invalid email address")),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - types are correct for yupResolver
    resolver: yupResolver(passwordResetSchema),
  });

  const { mutateAsync: passwordReset } = usePasswordReset();

  const onSubmit = async (data: PasswordResetFormData) => {
    const { email } = data;
    const remember = true;
    setEmailUsed(email);
    try {
      await passwordReset({ email, remember });
      onSuccess();
    } catch (error) {
      // TODO: move sentry to core and uncomment next line
      // logAppInfo(error.response?.data.detail);
      // recordAppError(error);
      // eslint-disable-next-line no-console
      console.log(error.response?.data.detail);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <FormControl isInvalid={Boolean(errors.email)}>
            <FormLabel htmlFor="email">{t("Email address")}</FormLabel>
            <Input
              {...field}
              id="email"
              placeholder={t("Email address")}
              data-testid="email-address-input"
            />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>
        )}
      />
      <Box h="element.4xl" />
      <Button
        width="100%"
        variant="solid"
        isLoading={isSubmitting}
        type="submit"
        data-testid="send-reset-link-button"
      >
        {t("Send reset link")}
      </Button>
    </form>
  );
}
